package features.booking

import features.BaseViewModel
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import network.FirebaseClient

class NotificacionViewModel : BaseViewModel() {

    private val _notificaciones = MutableStateFlow<List<Notificacion>>(emptyList())
    val notificaciones: StateFlow<List<Notificacion>> = _notificaciones.asStateFlow()

    private val _noLeidas = MutableStateFlow(0)
    val noLeidas: StateFlow<Int> = _noLeidas.asStateFlow()

    private var escuchaJob: Job? = null
    private var miUidActual: String = ""
    private var miRolActual: String = ""

    fun cargarNotificaciones(miUid: String, miRol: String) {
        // Si ya estamos escuchando con los mismos datos, no reiniciar
        if (escuchaJob?.isActive == true && miUid == miUidActual && miRol == miRolActual) return

        escuchaJob?.cancel()
        miUidActual = miUid
        miRolActual = miRol

        // FIX #12: Limpieza periódica al iniciar escucha
        limpiarNotificacionesViejas()

        escuchaJob = viewModelScope.launch {
            try {
                val db = FirebaseClient.database
                db.reference("notificaciones").orderByChild("timestamp").limitToLast(300).valueEvents.collect { snapshot ->
                    if (!snapshot.exists) {
                        _notificaciones.value = emptyList()
                        _noLeidas.value = 0
                        return@collect
                    }

                    val lista = mutableListOf<Notificacion>()
                    snapshot.children.forEach { child ->
                        try {
                            val id = child.key ?: return@forEach
                            val tipo = try { child.child("tipo").value<String>() } catch (e: Exception) { "" }
                            val titulo = try { child.child("titulo").value<String>() } catch (e: Exception) { "" }
                            val mensaje = try { child.child("mensaje").value<String>() } catch (e: Exception) { "" }
                            val timestamp = try { child.child("timestamp").value<String>().toLongOrNull() ?: 0L } catch (e: Exception) { 0L }
                            val destinatarioRol = try { child.child("destinatarioRol").value<String>() } catch (e: Exception) { "" }
                            val destinatarioUid = try { child.child("destinatarioUid").value<String>() } catch (e: Exception) { "" }

                            val esMia = destinatarioUid == miUid ||
                                    destinatarioRol == miRol ||
                                    destinatarioRol == "todos" ||
                                    (destinatarioRol == "admin" && (miRol == "admin" || miRol == "admin_general" || miRol == "master" || miRol == "desarrollador"))

                            if (esMia) {
                                val leida = try {
                                    child.child("leidaPor/$miUid").value<Boolean>()
                                } catch (e: Exception) { false }

                                lista.add(
                                    Notificacion(
                                        id = id,
                                        tipo = tipo,
                                        titulo = titulo,
                                        mensaje = mensaje,
                                        timestamp = timestamp,
                                        leida = leida,
                                        destinatarioRol = destinatarioRol,
                                        destinatarioUid = destinatarioUid
                                    )
                                )
                            }
                        } catch (e: Exception) {}
                    }

                    // Mostrar solo las 20 más recientes (evita saturar la bandeja).
                    val ordenadas = lista.sortedByDescending { it.timestamp }.take(20)
                    _notificaciones.value = ordenadas
                    _noLeidas.value = ordenadas.count { !it.leida }
                }
            } catch (e: Exception) {
            }
        }
    }

    fun detenerEscucha() {
        escuchaJob?.cancel()
        escuchaJob = null
    }

    fun marcarComoLeida(notificacionId: String, miUid: String) {
        viewModelScope.launch {
            try {
                FirebaseClient.database
                    .reference("notificaciones/$notificacionId/leidaPor/$miUid")
                    .setValue(true)
                _notificaciones.update { lista ->
                    lista.map { if (it.id == notificacionId) it.copy(leida = true) else it }
                }
                _noLeidas.value = _notificaciones.value.count { !it.leida }
            } catch (e: Exception) {}
        }
    }

    fun marcarTodasComoLeidas(miUid: String) {
        viewModelScope.launch {
            _notificaciones.value.filter { !it.leida }.forEach { notif ->
                try {
                    FirebaseClient.database
                        .reference("notificaciones/${notif.id}/leidaPor/$miUid")
                        .setValue(true)
                } catch (e: Exception) { println("Error marcando notificación leída: ${e.message}") }
            }
            _notificaciones.update { lista -> lista.map { it.copy(leida = true) } }
            _noLeidas.value = 0
        }
    }

    // FIX #12: Limpiar notificaciones con más de 30 días de antigüedad
    fun limpiarNotificacionesViejas() {
        viewModelScope.launch {
            try {
                val db = FirebaseClient.database
                val hace30Dias = org.example.abigail.obtenerMilisegundosActuales() - (30L * 24 * 60 * 60 * 1000)
                val snap = db.reference("notificaciones")
                    .orderByChild("timestamp")
                    .limitToFirst(50) // Borrar en lotes de 50
                    .valueEvents.first()
                if (snap.exists) {
                    val updates = mutableMapOf<String, Any?>()
                    snap.children.forEach { child ->
                        val ts = try { child.child("timestamp").value<String>().toLongOrNull() ?: 0L } catch (e: Exception) { 0L }
                        if (ts < hace30Dias) {
                            updates["notificaciones/${child.key}"] = null
                        }
                    }
                    if (updates.isNotEmpty()) {
                        db.reference().updateChildren(updates)
                    }
                }
            } catch (e: Exception) { /* best effort cleanup */ }
        }
    }

    companion object {
        suspend fun enviar(
            tipo: String,
            titulo: String,
            mensaje: String,
            destinatarioRol: String = "todos",
            destinatarioUid: String = ""
        ) {
            try {
                val db = FirebaseClient.database
                val ref = db.reference("notificaciones").push()
                val datos = mapOf(
                    "tipo" to tipo,
                    "titulo" to titulo,
                    "mensaje" to mensaje,
                    "timestamp" to org.example.abigail.obtenerMilisegundosActuales().toString(),
                    "destinatarioRol" to destinatarioRol,
                    "destinatarioUid" to destinatarioUid
                )
                // FIX #19: usar setValue en vez de updateChildren para escritura atómica a nivel de nodo
                ref.setValue(datos)
            } catch (e: Exception) {
            }
        }
    }
}
